/**
 * @file TeamAccessScreen.jsx
 * @description Team logins: what a teammate sees when they cannot work right now.
 *   - TeamAccessScreen (default): full screen, access paused (the agency is not on Pro)
 *     or removed by the owner. The server already shows them nothing (acting_agency_id
 *     falls back to their own id); this explains why.
 *   - OwnerOnlyNotice: in place of an owner-only screen (Billing, Agency Profile) or action.
 * @module components/team/TeamAccessScreen
 */
import { useTranslation } from "react-i18next";
import { T } from "../../theme";
import { Btn } from "../UI";
import Logo from "../Logo";
import { AlertTriangleIcon } from "../Icons";
import { useModalViewport } from "../../hooks/useModalViewport";
import { LAYER_PAGE_MODAL } from "../../lib/layers";

export default function TeamAccessScreen({ context, onSignOut }) {
  const { t } = useTranslation();
  const agency = context?.agencyName || t("team.join.yourAgency");
  const removed = context?.status === "removed";
  return (
    <div style={{
      minHeight: "100vh", background: T.bg, fontFamily: T.fontBody, color: T.text,
      padding: "32px 16px", display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <div style={{ marginBottom: 20 }}>
        <Logo variant="lockup" height={30} alt="ABAcases" />
      </div>
      <div style={{
        width: "100%", maxWidth: 460, background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: T.radiusLg, boxShadow: T.shadow, padding: "26px 22px 24px", boxSizing: "border-box",
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: T.radiusFull, background: `${T.yellow}14`,
          display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14,
        }}>
          <AlertTriangleIcon size={20} color={T.yellow} />
        </div>
        <h1 style={{ margin: 0, fontSize: 20, lineHeight: 1.3, fontWeight: 800, letterSpacing: "-0.02em" }}>
          {removed ? t("team.access.removedTitle") : t("team.access.pausedTitle")}
        </h1>
        <p style={{ margin: "12px 0 0", fontSize: 14.5, lineHeight: 1.6, color: T.textMid }}>
          {removed ? t("team.access.removedBody", { agency }) : t("team.access.pausedBody", { agency })}
        </p>
        <div style={{ marginTop: 20 }}>
          <Btn variant="outline" onClick={onSignOut} style={{ width: "100%", padding: "13px 20px", fontSize: 15.5, borderRadius: T.radiusMd }}>
            {t("team.join.signOut")}
          </Btn>
        </div>
      </div>
    </div>
  );
}

/**
 * A teammate tried to open an admin-only screen (Billing, buying credits, the agency profile, Team): say so in
 * place and stay on the page. No navigation, so Back keeps working (App.setView).
 */
export function OwnerOnlyDialog({ onClose }) {
  const { t } = useTranslation();
  const vp = useModalViewport({ active: true });
  return (
    <div role="presentation" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: LAYER_PAGE_MODAL, background: "rgba(15,23,42,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-labelledby="owner-only-title" data-testid="owner-only-dialog"
        style={{ background: T.surface, borderRadius: T.radiusLg, boxShadow: T.shadow, padding: "20px 20px 16px",
          width: "min(440px, 100%)", maxHeight: vp.maxH(0.9), overflowY: "auto", fontFamily: T.fontBody }}>
        <div id="owner-only-title" style={{ fontSize: 17, fontWeight: 800, color: T.text }}>{t("team.access.ownerOnlyTitle")}</div>
        <p style={{ margin: "10px 0 16px", fontSize: 14, lineHeight: 1.6, color: T.textMid }}>{t("team.access.ownerOnlyBody")}</p>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Btn variant="outline" onClick={onClose}>{t("team.access.gotIt")}</Btn>
        </div>
      </div>
    </div>
  );
}

export function OwnerOnlyNotice({ context, onBack, body }) {
  const { t } = useTranslation();
  return (
    <div style={{ maxWidth: 520, margin: "48px auto", padding: "0 16px", fontFamily: T.fontBody }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, boxShadow: T.shadow, padding: "22px 20px" }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>{t("team.access.ownerOnlyTitle")}</div>
        <p style={{ margin: "10px 0 0", fontSize: 14, lineHeight: 1.6, color: T.textMid }}>{body || t("team.access.ownerOnlyBody")}</p>
        {onBack && (
          <div style={{ marginTop: 16 }}>
            <Btn variant="outline" onClick={onBack}>{t("common.back", "Back")}</Btn>
          </div>
        )}
      </div>
    </div>
  );
}
